// @ts-nocheck
/**
 * chartSeriesPipeline.ts — Dual-mode chart series construction (DEC-056 addendum).
 *
 * Shared helpers: CSV/scalar/list normalize, frequencies, limit filters, true histogram bins.
 */

import { StatisticsHelpers } from '../../../../../shims/appStub';
import type { PlotSeries } from '../../../../../shims/appStub';
import type { LayoutChartType } from '../../types/component';
import type { LayoutChartSeriesPoint } from './chartSeriesFromRows';

/** Labels emitted for each five-number summary slot in a box plot series. */
export const BOX_SUMMARY_LABELS = ['min', 'q1', 'median', 'q3', 'max'] as const;
export type BoxSummaryLabel = (typeof BOX_SUMMARY_LABELS)[number];

export function parseCsvStatic(raw: string): Array<string | number | null> {
  if (raw.trim() === '') return [];
  return raw.split(',').map((part) => {
    const trimmed = part.trim();
    if (trimmed === '') return null;
    const asNum = Number(trimmed);
    if (trimmed !== '' && Number.isFinite(asNum) && /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(trimmed)) {
      return asNum;
    }
    return trimmed;
  });
}

/** Normalize a resolved ValueSource value into a flat list. */
export function normalizeSourceToList(raw: unknown): Array<string | number | null> {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map((item) => {
      if (item == null) return null;
      if (typeof item === 'number' && Number.isFinite(item)) return item;
      if (typeof item === 'string') return item;
      if (typeof item === 'boolean') return item ? 1 : 0;
      return String(item);
    });
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) return [raw];
  if (typeof raw === 'boolean') return [raw ? 1 : 0];
  if (typeof raw === 'string') {
    if (raw.includes(',')) return parseCsvStatic(raw);
    const asNum = Number(raw.trim());
    if (raw.trim() !== '' && Number.isFinite(asNum) && /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(raw.trim())) {
      return [asNum];
    }
    return [raw];
  }
  return [String(raw)];
}

function toYNumber(raw: string | number | null): number | null {
  if (raw == null) return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function toXValue(raw: string | number | null): string | number | null {
  if (raw == null) return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') return raw;
  return String(raw);
}

function numericX(raw: string | number | null): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export type ChartLimitFilters = {
  xMin?: number;
  xMax?: number;
  yMin?: number;
  yMax?: number;
};

export function applyLimitFilters(
  series: LayoutChartSeriesPoint[],
  limits: ChartLimitFilters,
): LayoutChartSeriesPoint[] {
  const { xMin, xMax, yMin, yMax } = limits;
  if (
    xMin == null &&
    xMax == null &&
    yMin == null &&
    yMax == null
  ) {
    return series;
  }
  return series.filter((point) => {
    if (yMin != null && point.y < yMin) return false;
    if (yMax != null && point.y > yMax) return false;
    const xNum = numericX(point.x);
    if (xNum != null) {
      if (xMin != null && xNum < xMin) return false;
      if (xMax != null && xNum > xMax) return false;
    }
    return true;
  });
}

/** Frequency-count a list of keys → series with label + count. */
export function buildFrequencySeries(
  values: Array<string | number | null>,
): LayoutChartSeriesPoint[] {
  const counts = new Map<string, { key: string | number; count: number }>();
  for (const raw of values) {
    if (raw == null) continue;
    if (typeof raw === 'string' && raw.trim() === '') continue;
    const key = String(raw);
    const existing = counts.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      counts.set(key, {
        key: typeof raw === 'number' ? raw : raw,
        count: 1,
      });
    }
  }
  return Array.from(counts.values()).map(({ key, count }) => ({
    x: key,
    y: count,
    label: String(key),
  }));
}

/** Equal-width true histogram bins over numeric values. */
export function buildHistogramBins(
  values: Array<string | number | null>,
  binCount: number,
  limits: ChartLimitFilters,
): LayoutChartSeriesPoint[] {
  const nums: number[] = [];
  for (const raw of values) {
    const n = toYNumber(raw);
    if (n == null) continue;
    if (limits.yMin != null && n < limits.yMin) continue;
    if (limits.yMax != null && n > limits.yMax) continue;
    nums.push(n);
  }
  if (nums.length === 0) return [];

  const count = Math.max(1, Math.floor(binCount) || 10);
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  if (min === max) {
    return [{ x: String(min), y: nums.length, label: String(min) }];
  }

  const width = (max - min) / count;
  const bins = Array.from({ length: count }, () => 0);
  for (const n of nums) {
    let idx = Math.floor((n - min) / width);
    if (idx >= count) idx = count - 1;
    if (idx < 0) idx = 0;
    bins[idx]! += 1;
  }

  return bins.map((binCountValue, i) => {
    const lo = min + i * width;
    const hi = i === count - 1 ? max : min + (i + 1) * width;
    const label = `${lo.toPrecision(4)}–${hi.toPrecision(4)}`;
    return {
      x: lo,
      y: binCountValue,
      label,
    };
  });
}

/**
 * Box-plot series: five points per group with labels min|q1|median|q3|max.
 * Whiskers use sample min/max (Tukey hinges for quartiles).
 * yValues preferred; if empty, xValues are treated as the distribution.
 * When category keys (x or label) are present alongside y, one box per category.
 */
export function buildBoxPlotSeries(
  xValues: Array<string | number | null>,
  yValues: Array<string | number | null>,
  labelValues: Array<string | number | null> | null | undefined,
  limits: ChartLimitFilters,
): LayoutChartSeriesPoint[] {
  const useY = yValues.length > 0;
  const valueSource = useY ? yValues : xValues;
  if (valueSource.length === 0) return [];

  const groups = new Map<string, { key: string | number; values: number[] }>();
  const order: string[] = [];

  const hasCategoryAxis =
    useY &&
    (xValues.length > 0 || (labelValues != null && labelValues.length > 0));

  for (let i = 0; i < valueSource.length; i += 1) {
    const y = toYNumber(valueSource[i] ?? null);
    if (y == null) continue;
    if (limits.yMin != null && y < limits.yMin) continue;
    if (limits.yMax != null && y > limits.yMax) continue;

    let groupKey: string | number = 0;
    if (hasCategoryAxis) {
      const labelRaw = labelValues?.[i];
      const xRaw = xValues[i] ?? null;
      if (labelRaw != null && String(labelRaw) !== '') {
        groupKey = typeof labelRaw === 'number' ? labelRaw : String(labelRaw);
      } else if (xRaw != null && String(xRaw) !== '') {
        groupKey = typeof xRaw === 'number' ? xRaw : String(xRaw);
      } else {
        groupKey = i;
      }
    }

    const mapKey = String(groupKey);
    let group = groups.get(mapKey);
    if (!group) {
      group = { key: groupKey, values: [] };
      groups.set(mapKey, group);
      order.push(mapKey);
    }
    group.values.push(y);
  }

  const series: LayoutChartSeriesPoint[] = [];
  for (const mapKey of order) {
    const group = groups.get(mapKey)!;
    if (group.values.length === 0) continue;
    const [min, q1, median, q3, max] = StatisticsHelpers.fiveNumberSummary(group.values);
    const slots: Array<{ label: BoxSummaryLabel; y: number }> = [
      { label: 'min', y: min },
      { label: 'q1', y: q1 },
      { label: 'median', y: median },
      { label: 'q3', y: q3 },
      { label: 'max', y: max },
    ];
    for (const slot of slots) {
      series.push({
        x: group.key,
        y: slot.y,
        label: slot.label,
      });
    }
  }
  return series;
}

export type BuildPlottedSeriesInput = {
  chartType: LayoutChartType;
  xValues: Array<string | number | null>;
  yValues: Array<string | number | null>;
  labelValues?: Array<string | number | null> | null;
  colorValues?: Array<string | number | null> | null;
  limits: ChartLimitFilters;
  binCount: number;
};

/**
 * Build final plot series for a chart type. Never throws on unequal lengths.
 */
export function buildPlottedSeries(input: BuildPlottedSeriesInput): LayoutChartSeriesPoint[] {
  const {
    chartType,
    xValues,
    yValues,
    labelValues,
    colorValues,
    limits,
    binCount,
  } = input;

  if (chartType === 'histogram') {
    const source = yValues.length > 0 ? yValues : xValues;
    return buildHistogramBins(source, binCount, limits);
  }

  if (chartType === 'box') {
    return buildBoxPlotSeries(xValues, yValues, labelValues, limits);
  }

  if (chartType === 'barFrequencies' || chartType === 'pieFrequencies') {
    const source = yValues.length > 0 ? yValues : xValues;
    return applyLimitFilters(buildFrequencySeries(source), limits);
  }

  // values / line / scatter / timeline / bar / pie: zip X with Y
  const useIndexX = xValues.length === 0;
  const len = useIndexX
    ? yValues.length
    : Math.min(xValues.length, yValues.length);

  if (len === 0) return [];

  const series: LayoutChartSeriesPoint[] = [];
  for (let i = 0; i < len; i += 1) {
    const y = toYNumber(yValues[i] ?? null);
    if (y == null) continue;
    if ((chartType === 'pie') && y <= 0) continue;

    const point: LayoutChartSeriesPoint = {
      x: useIndexX ? i : toXValue(xValues[i] ?? null),
      y,
    };
    const label = labelValues ? (labelValues[i] != null ? String(labelValues[i]) : null) : null;
    if (label != null && label !== '') point.label = label;
    else if (!useIndexX && point.x != null) point.label = String(point.x);

    const color = colorValues
      ? colorValues[i] != null
        ? String(colorValues[i])
        : null
      : null;
    if (color != null && color !== '') point.color = color;

    series.push(point);
  }

  return applyLimitFilters(series, limits);
}

export type MultiSeriesYInput = {
  id: string;
  yValues: Array<string | number | null>;
  label?: string;
  color?: string;
  strokeWidth?: number;
  yAxis?: 'primary' | 'secondary';
  yMin?: number;
  yMax?: number;
};

export type BuildMultiSeriesPlotInput = {
  chartType: LayoutChartType;
  xValues: Array<string | number | null>;
  seriesInputs: MultiSeriesYInput[];
  labelValues?: Array<string | number | null> | null;
  colorValues?: Array<string | number | null> | null;
  xMin?: number;
  xMax?: number;
  binCount: number;
};

/**
 * Build named PlotSeries[] for multi-series XY (or wrap single-series categorical).
 * Max 8 series. Non-XY chart types use only the first series input.
 */
export function buildMultiSeriesPlot(input: BuildMultiSeriesPlotInput): PlotSeries[] {
  const { chartType, xValues, seriesInputs, labelValues, colorValues, xMin, xMax, binCount } = input;
  const capped = seriesInputs.slice(0, 8);
  if (capped.length === 0) return [];

  const isXy =
    chartType === 'line' || chartType === 'scatter' || chartType === 'timeline';

  if (!isXy) {
    const first = capped[0]!;
    const points = buildPlottedSeries({
      chartType,
      xValues,
      yValues: first.yValues,
      labelValues,
      colorValues,
      limits: {
        xMin,
        xMax,
        yMin: first.yMin,
        yMax: first.yMax,
      },
      binCount,
    });
    return [
      {
        id: first.id || 'y',
        label: first.label,
        color: first.color,
        strokeWidth: first.strokeWidth,
        yAxis: 'primary',
        points,
      },
    ];
  }

  return capped.map((s, index) => {
    const points = buildPlottedSeries({
      chartType,
      xValues,
      yValues: s.yValues,
      labelValues: index === 0 ? labelValues : null,
      colorValues: index === 0 ? colorValues : null,
      limits: {
        xMin,
        xMax,
        yMin: s.yMin,
        yMax: s.yMax,
      },
      binCount,
    });
    return {
      id: s.id || `s${index}`,
      label: s.label,
      color: s.color,
      strokeWidth: s.strokeWidth,
      yAxis: s.yAxis === 'secondary' ? 'secondary' : 'primary',
      points,
    };
  });
}
